import type { ScreenSchema } from './types';

/**
 * Schema for the `tabs` template.
 *
 * Slot → audio-marker key convention:
 *   'intro'  → screen.id          (screen heading/text/instructions)
 *   'tab-0'  → ${screen.id}-tab-0 (first tab content)
 *   'tab-1'  → ${screen.id}-tab-1 (second tab content)
 *    … etc.
 *
 * Tab audio is triggered by the `open(i)` handler on tab click, not on section
 * activation. Only the intro slot plays automatically when the section scrolls in.
 *
 * The tab label is omitted from the intro narration (it repeats on tab click).
 * On click, the label is included in the tab slot only when no heading/subheading/title
 * is present (matching the original buildTabNarration logic).
 */
const tabsSchema: ScreenSchema = {
  template: 'tabs',
  entryPrefetch: 'intro-and-initial-tab',
  // Tabs manages its own section audio: intro plays first, then initial tab is
  // queued to follow. This requires sequential await logic that can't be
  // handled generically by uc-screen.vue without race conditions.
  managesOwnSectionAudio: true,
  slots: {
    intro: {
      parts: [
        { field: 'heading', fallback: ['subheading', 'title'] },
        { field: 'text' },
        { field: 'instructions' }
      ]
    },

    tab: {
      iterate: 'tabs',
      keyPrefix: 'tab',
      parts: [
        // Include the tab label only when there is no heading/subheading/title
        // (matching existing buildTabNarration behaviour)
        {
          field: 'label',
          omitIfPresent: ['heading', 'subheading', 'title']
        },
        { field: 'heading', fallback: ['subheading', 'title'] },
        { field: 'text' },
        { field: 'instructions' },
        // Fold a tab's nested `templates[]` (e.g. an image-list) into its clip —
        // uc-content renders these as nested (silent) screens, so their content
        // is only narrated here. Shallow by design (see cards schema).
        {
          iterateField: 'templates',
          parts: [
            { field: 'heading', fallback: ['subheading', 'title'] },
            { field: 'text' },
            { field: 'instructions' },
            // image-list captions rendered inside the nested template
            { iterateField: 'items', parts: [{ field: 'text' }] }
          ]
        }
      ]
    }
  }
};

export default tabsSchema;
